import {
  Events,
  EventParticipants,
} from "@/integrations/supabase/types/events";
import { HostProfiles } from "@/integrations/supabase/types/profiles";
import {
  OpportunityCatalog,
  Opportunities,
} from "@/integrations/supabase/types/opportunities";

type Event = Events["Row"] & {
  event_participants?: EventParticipants["Row"][];
  opportunity_catalog?: OpportunityCatalog["Row"] & {
    opportunity?: Opportunities["Row"];
  };
  host?: HostProfiles["Row"];
};

type EventId = {
  id: string;
};

const EVENT_PARTICIPANT_FIELDS = `
  name,
  picture_url,
  status,
  remarks
`;

const EVENT_NPO_FIELDS = `
  id,
  org_name,
  email,
  phone,
  address_line1,
  address_line2,
  city,
  state,
  country,
  zip_code
`;

const EVENT_CORPORATE_CONTACT_FIELDS = `
  id,
  name,
  email,
  contact,
  designation
`;

const EVENT_CORPORATE_FIELDS = `*`;

const EVENT_HOST_FIELDS = `*`;

const EVENT_CATALOG_FIELDS = `
  id,
  event_cost,
  volunteer_output,
  format,
  format_details,
  min_volunteers,
  max_volunteers,
  host_included,
  opportunities!opportunity_catalog_opportunity_id_fkey (
    id,
    name,
    type,
    status,
    focus_areas,
    beneficiary_types
  )
`;

const EVENT_PAYMENT_FIELDS = `
  amount,
  status
`;

interface CreateEventInput {
  event_name: string;
  description: string;
  event_date: string;
  event_time: string;
  corporate_id: string;
  npo_id: string;
  opportunity_catalog_id: string;
  status?: string;
}

interface UpdateEventInput extends Partial<CreateEventInput> {
  id: string;
}

const EVENT_SELECT_QUERY = `
  *,
  opportunity_catalog!events_opportunity_catalog_id_fkey (
    id,
    location,
    event_cost,
    volunteer_output,
    opportunities!opportunity_catalog_opportunity_id_fkey (
      id,
      name,
      duration,
      format,
      type,
      status,
      focus_areas,
      beneficiary_types
    )
  ),
  npo_profiles!events_npo_id_fkey (
    id,
    org_name,
    email,
    phone,
    address_line1,
    address_line2,
    city,
    state,
    country,
    zip_code
  )
`;

export const getEventById = async (id: string, access_token: string) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${id}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data as Event;
  } catch (error) {
    console.error("Error fetching event:", error);
    return null;
  }
};

const getUpcomingEvents = async (
  access_token: string,
  limit?: number,
  page?: number,
  eventType?: string[]
) => {
  try {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append("limit", limit.toString());
    if (page) queryParams.append("page", page.toString());
    if (eventType && eventType.length > 0) {
      eventType.forEach(type => queryParams.append("eventType", type));
    }

    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching upcoming events:", error);
    return {
      data: [],
      pagination: { page: 1, limit: 10, total: 0, totalPages: 1 },
    };
  }
};

export const getUpcomingCorporateEvents = (
  access_token: string,
  limit?: number,
  page?: number,
  eventType?: string[]
) => getUpcomingEvents(access_token, limit, page, eventType);

export const getUpcomingHostEvents = (
  access_token: string,
  limit?: number,
  page?: number
) => getUpcomingEvents(access_token, limit, page);

export const getUpcomingNPOEvents = (
  access_token: string,
  limit?: number,
  page?: number
) => getUpcomingEvents(access_token, limit, page);

export const getAnonEvents = (
  access_token: string,
  limit?: number,
  page?: number
) => getUpcomingEvents(access_token, limit, page);

export const getHostEvents = async (
  access_token: string,
  limit?: number,
  page?: number
) => {
  return getUpcomingEvents(access_token, limit, page);
};

export interface CreateEventData {
  opportunity_catalog_id: string;
  npo_id: string;
  event_name: string;
  description: string;
  corporate_id: string;
  status: string;
  host_id?: string;
  event_date: string;
  event_time: string;
  paylater: boolean;
  location?: string;
  campaign_id?: string;
  preferred_language?: string;
  expected_participants?: number;
  meta?: Record<string, any>;
}

export const createEvent = async (eventData: CreateEventData) => {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/events`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        opportunity_catalog_id: eventData.opportunity_catalog_id,
        npo_id: eventData.npo_id,
        event_name: eventData.event_name,
        description: eventData.description,
        corporate_id: eventData.corporate_id,
        status: eventData.status,
        event_date: eventData.event_date,
        event_time: eventData.event_time,
        location: eventData.location,
        paylater: Boolean(eventData.paylater) || false,
        host_id: eventData.host_id || null,
        campaign_id: eventData.campaign_id || null,
        preferred_language: eventData.preferred_language || null,
        expected_participants: eventData.expected_participants || null,
        meta: eventData.meta || null,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create event");
  }

  const eventId = await response.json();
  return { id: eventId.data, error: null };
};

export const updateEventWithInvites = async ({
  id,
  eventData,
}: {
  id: string;
  eventData: CreateEventData;
}) => {
  console.log("Updating event with invites:", eventData);
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/events/${id}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        opportunity_catalog_id: eventData.opportunity_catalog_id,
        npo_id: eventData.npo_id,
        event_name: eventData.event_name,
        description: eventData.description,
        corporate_id: eventData.corporate_id,
        status: eventData.status,
        event_date: eventData.event_date,
        event_time: eventData.event_time,
        location: eventData.location,
        paylater: Boolean(eventData.paylater) || false,
        host_id: eventData.host_id || null,
        campaign_id: eventData.campaign_id || null,
        preferred_language: eventData.preferred_language || null,
        expected_participants: eventData.expected_participants || null,
        meta: eventData.meta || null,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update event");
  }

  const data = await response.json();
  return { data: data.data, error: null };
};

/**
 * Get the access token for API requests
 * @param token - The session access token
 * @returns The access token
 */
export async function getAccessToken(token?: string) {
  if (token) {
    return token;
  }
  return null;
}

/**
 * Get event participants with attendance status
 * @param eventId - The ID of the event
 * @param token - The session access token
 * @returns List of event participants with attendance status
 */
export async function getEventAttendance(eventId: string, token?: string) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${eventId}/attendance`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get event attendance: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching event attendance:", error);
    throw error;
  }
}

/**
 * Update attendance status for a single participant
 * @param eventId - The ID of the event
 * @param participantId - The ID of the participant
 * @param status - The new attendance status
 * @param remarks - Optional remarks about the attendance
 * @param token - The session access token
 * @returns Updated participant data
 */
export async function updateParticipantStatus(
  eventId: string,
  participantId: string,
  status: "signed_up" | "attended" | "no_show" | "opt_out",
  remarks?: string,
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_CC_API_URL
      }/v1/events/${eventId}/attendance/participant`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          participantId,
          status,
          remarks,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update participant status: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error updating participant status:", error);
    throw error;
  }
}

/**
 * Update attendance status for multiple participants at once
 * @param eventId - The ID of the event
 * @param participantIds - Array of participant IDs to update
 * @param status - The new attendance status
 * @param remarks - Optional remarks about the attendance
 * @param token - The session access token
 * @returns Count of updated records
 */
export async function bulkUpdateParticipantStatus(
  eventId: string,
  participantIds: string[],
  status: "signed_up" | "attended" | "no_show" | "opt_out",
  remarks?: string,
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${eventId}/attendance/bulk`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          participantIds,
          status,
          remarks,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to bulk update participant status: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error bulk updating participant status:", error);
    throw error;
  }
}

/**
 * Add a single participant to an event
 */
export async function addParticipant(
  eventId: string,
  participant: {
    name: string;
    email: string;
    phone?: string;
  },
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_CC_API_URL
      }/v1/events/${eventId}/attendance/participant`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(participant),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to add participant");
    }

    return response.json();
  } catch (error) {
    console.error("Error adding participant:", error);
    throw error;
  }
}

/**
 * Bulk add participants to an event
 */
export async function bulkAddParticipants(
  eventId: string,
  participants: Array<{
    name: string;
    email: string;
    phone?: string;
    status?: "signed_up" | "attended" | "no_show" | "opt_out";
  }>,
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${eventId}/attendance/bulk`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ participants }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to bulk add participants");
    }

    return response.json();
  } catch (error) {
    console.error("Error bulk adding participants:", error);
    throw error;
  }
}

// Event Checklist Types and Functions
export interface EventChecklistItem {
  id: string;
  item: string; // Task description
  notes?: string;
  owner: "npo" | "corporate" | "host" | "cc";
  outdoor: boolean;
  virtual: boolean;
  inOffice: boolean;
  timeline: string;
  completed: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
  eventPhase: "Pre" | "During" | "Post";
  priority: "High" | "Medium" | "Low";
  updateType:
    | "EVENT_PHOTOS"
    | "EVENT_BROCHURES"
    | "EVENT_LOCATION"
    | "EVENT_MEET_LINK"
    | "EVENT_HOST_DETAILS"
    | "EVENT_UPDATE"
    | "OTHERS"
    | "EVENT_COLLATERALS"
    | "THANKYOU"
    | "EVENT_SHIPMENTS"
    | "POST_EVENT_IMPACT"
    | "ATTENDANCE"
    | "VOLUNTEER_SUBMISSION_LINK";
  reminderSent: boolean;
}

export interface EventChecklistData {
  items: EventChecklistItem[];
  eventType: string;
  lastUpdated: string;
}

/**
 * Get event checklist items and summary
 * @param eventId - The ID of the event
 * @param token - The session access token
 * @returns Event checklist data
 */
export async function getEventChecklist(
  eventId: string,
  token?: string
): Promise<{ checklist: EventChecklistData; summary: any }> {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${eventId}/checklist`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch event checklist: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching event checklist:", error);
    throw error;
  }
}

/**
 * Update checklist item completion status
 * @param eventId - The ID of the event
 * @param itemId - The ID of the checklist item
 * @param completed - The completion status
 * @param token - The session access token
 * @returns Updated checklist item
 */
export async function updateChecklistItem(
  eventId: string,
  itemId: string,
  completed: boolean,
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/events/${eventId}/checklist/item`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          itemId,
          completed,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update checklist item: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating checklist item:", error);
    throw error;
  }
}

/**
 * Get event checklist summary
 * @param eventId - The ID of the event
 * @param token - The session access token
 * @returns Event checklist summary
 */
export async function getEventChecklistSummary(
  eventId: string,
  token?: string
) {
  const accessToken = await getAccessToken(token);

  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_CC_API_URL
      }/v1/events/${eventId}/checklist/summary`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch checklist summary: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching checklist summary:", error);
    throw error;
  }
}
